import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectFootTrafficVenueCoverage } from '@/store/selectors/attribution/footTraffic.selectors';
import { getFootTrafficVenueCoverage } from '@/store/slices/attribution/footTraffic.slice';
import { Close } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

const VenueCoverageModal = ({ isOpen, onClose }: IProps) => {
  const dispatch = useAppDispatch();
  const { loading, summary, locations } = useAppSelector(selectFootTrafficVenueCoverage);

  useEffect(() => {
    if (isOpen) {
      dispatch(getFootTrafficVenueCoverage({}));
    }
  }, [isOpen, dispatch]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Venue coverage
        <IconButton onClick={onClose} aria-label='close'>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box>
            <Skeleton variant='text' width='40%' />
            <Skeleton variant='rectangular' height={200} sx={{ mt: 2 }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
              <Typography variant='body2'>
                <strong>{summary.total}</strong> total locations
              </Typography>
              <Typography variant='body2' sx={{ color: 'success.main' }}>
                <strong>{summary.withData}</strong> with data
              </Typography>
              <Typography variant='body2' sx={{ color: 'error.main' }}>
                <strong>{summary.withoutData}</strong> missing data
              </Typography>
            </Box>
            {locations.length === 0 ? (
              <Typography variant='body2' sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                No eligible locations for the current filters.
              </Typography>
            ) : (
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Location</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>City</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell align='center'>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locations.map(loc => (
                      <TableRow key={loc.id} hover>
                        <TableCell>{loc.name}</TableCell>
                        <TableCell>{loc.address}</TableCell>
                        <TableCell>{loc.city}</TableCell>
                        <TableCell>{loc.state}</TableCell>
                        <TableCell align='center'>
                          <Chip
                            size='small'
                            label={loc.hasData ? 'Has data' : 'Missing'}
                            color={loc.hasData ? 'success' : 'error'}
                            variant='outlined'
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VenueCoverageModal;
